import React from 'react';
import { Pack } from '../types';
import { motion } from 'framer-motion';
import { PlusIcon } from './icons';

interface PackCardProps {
  pack: Pack;
  restaurantName: string;
  onPress?: (pack: Pack) => void;
  onAddToCart?: (pack: Pack) => void;
  isOffline?: boolean;
}

const PackCard: React.FC<PackCardProps> = ({ pack, restaurantName, onPress, onAddToCart, isOffline = false }) => {
  const discount = pack.originalPrice - pack.packPrice;
  const discountPercentage = Math.round((discount / pack.originalPrice) * 100);

  return (
    <motion.div
      whileHover={{ y: -5 }}
      whileTap={{ scale: 0.98 }}
      className={`bg-white rounded-[2.5rem] shadow-[0_15px_35px_-10px_rgba(0,0,0,0.1)] overflow-hidden flex flex-col w-[280px] h-[520px] flex-shrink-0 border border-gray-100 group isolation-isolate relative z-0 ${isOffline ? 'grayscale opacity-80' : ''}`}
      onClick={() => onPress?.(pack)}
    >
      {/* Visual Header */}
      <div className="relative h-56 w-full overflow-hidden flex-shrink-0">
        {pack.image ? (
          <img 
            src={pack.image} 
            alt={pack.name} 
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
            style={{ transform: 'translateZ(0)' }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center text-5xl">🎁</div>
        )}

        {/* [NEW] Closed Badge for Items */}
        {isOffline && (
            <div className="absolute inset-0 flex items-center justify-center z-30">
                <span className="bg-black/60 backdrop-blur-sm text-white text-xs font-black uppercase px-4 py-1.5 rounded-full border border-white/20">Restaurant Fermé</span>
            </div>
        )}
        
        {/* Floating Badges */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          <div className="bg-red-600 text-white text-[10px] font-black px-3 py-1.5 rounded-full shadow-lg uppercase tracking-widest italic animate-pulse">
            Mega Deal
          </div>
          {discountPercentage > 0 && (
            <div className="bg-black/80 backdrop-blur-md text-white text-[10px] font-black px-3 py-1.5 rounded-full shadow-lg uppercase tracking-widest border border-white/20">
              -{discountPercentage}%
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1 overflow-hidden">
        <h3 className="text-xl font-black text-gray-900 mb-0.5 leading-tight group-hover:text-red-600 transition-colors uppercase tracking-tighter italic shrink-0">
          {pack.name}
        </h3>
        <p className="text-[10px] text-red-600 font-black uppercase tracking-widest mb-2 italic shrink-0">
          {restaurantName}
        </p>
        
        {/* Scrollable Items Section */}
        <div className="flex-1 overflow-y-auto no-scrollbar mb-4 pr-1">
            <div className="flex flex-col gap-2">
              {/* Included Dishes */}
              {pack.dishes && pack.dishes.filter(Boolean).map((dish, idx) => (
                <div key={`${dish.id || idx}-${idx}`} className="flex items-baseline gap-2">
                  <span className="text-xl font-black text-red-600 italic leading-none">{dish.quantity || 1}x</span>
                  <span className="text-xs font-black text-gray-900 uppercase tracking-tighter leading-tight">
                    {dish.category} {dish.name}
                  </span>
                </div>
              ))}

              {/* Extras */}
              {pack.extras && pack.extras.length > 0 && pack.extras.filter(Boolean).map((extra, idx) => (
                <div key={extra.id || `extra-${idx}`} className="flex items-baseline gap-2">
                  <span className="text-xl font-black text-red-600 italic leading-none">{extra.quantity || 1}x</span>
                  <span className="text-xs font-black text-gray-900 uppercase tracking-tighter leading-tight">
                    {extra.name}
                  </span>
                </div>
              ))}
            </div>
        </div>

        <div className="mt-auto flex items-center justify-between shrink-0">
          <div className="flex flex-col">
            <span className="text-[10px] font-black text-gray-400 line-through uppercase tracking-widest mb-0.5">
              {pack.originalPrice} DA
            </span>
            <span className="text-2xl font-black text-red-600 italic tracking-tighter">
              {pack.packPrice} <span className="text-xs">DA</span>
            </span>
          </div>

          {!isOffline && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddToCart?.(pack);
              }}
              className="w-12 h-12 bg-red-600 text-white rounded-full flex items-center justify-center shadow-xl shadow-red-100 hover:scale-110 active:scale-95 transition-all group/btn"
            >
              <PlusIcon className="w-6 h-6 transition-transform group-hover/btn:rotate-90" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default PackCard;
